import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, MoreHorizontal, UserCog, Key, Trash2, 
  Edit, LogIn, ChevronLeft, ChevronRight, ArrowUpDown, X, Shield, ShieldCheck, Crown, Coins, AlertTriangle, Users, Languages, School, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  { value: 'all', label: 'All Roles' },
  { value: 'master_admin', label: 'Master Admin' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const PLANS = [
  { value: 'all', label: 'All Plans' },
  { value: 'freemium', label: 'Freemium' },
  { value: 'free', label: 'Free (Legacy)' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'reseller', label: 'Reseller' },
];

const STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending', label: 'Pending' },
];

const SORT_OPTIONS = [
  { value: 'recently_created', label: 'Recently Created' },
  { value: 'alpha', label: 'Alphabetical (Name)' },
  { value: 'email', label: 'Email' },
  { value: 'role', label: 'Role' },
  { value: 'plan', label: 'Plan' },
];

const PAGE_SIZES = [10, 20, 50, 100];

const UserManagement = () => {
  const { user: currentUser, setToken, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'master_admin';
  const isMasterAdmin = currentUser?.role === 'master_admin';

  // Data state - ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter & pagination state
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recently_created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(10);
  const [creditsReason, setCreditsReason] = useState('');
  const [currentCredits, setCurrentCredits] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Translation settings state
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [translationSettings, setTranslationSettings] = useState({
    translation_enabled: false,
    translation_tier: 'free',
    translation_positions: ['floating'],
    default_language: 'en',
    enabled_languages: null,
    translation_mode: 'selector' // 'selector' (manual) or 'auto' (browser/IP detection)
  });
  const [isUpdatingTranslation, setIsUpdatingTranslation] = useState(false);

  // Wonde MIS settings state
  const [showWondeModal, setShowWondeModal] = useState(false);
  const [wondeStatus, setWondeStatus] = useState(null);
  const [isTogglingWonde, setIsTogglingWonde] = useState(false);

  // Promote to Reseller state
  const [showPromoteResellerModal, setShowPromoteResellerModal] = useState(false);
  const [promoteResellerData, setPromoteResellerData] = useState({ company_name: '', slug: '', commission_rate: '50' });
  const [isPromoting, setIsPromoting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company: '',
    role: 'editor',
    plan: 'starter',
    status: 'active',
    billing_type: 'free'
  });
  const [newPassword, setNewPassword] = useState('');

  // Redirect non-super-admins
  useEffect(() => {
    if (currentUser && !isSuperAdmin) {
      toast.error('Access denied. Super Admin privileges required.');
      navigate('/dashboard');
    }
  }, [currentUser, isSuperAdmin, navigate]);

  const fetchUsers = useCallback(async () => {
    if (!isSuperAdmin) return; // Don't fetch if not super admin
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (roleFilter !== 'all') params.append('role_filter', roleFilter);
      if (planFilter !== 'all') params.append('plan_filter', planFilter);
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (search) params.append('search', search);

      const res = await api.get(`/users?${params.toString()}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortOrder, roleFilter, planFilter, statusFilter, search, isSuperAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleCreateUser = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      await api.post('/users', formData);
      toast.success('Team member created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const response = await api.post('/users/create-customer', formData);
      toast.success(`New customer account created: ${response.data.email}. They have their own independent workspace.`);
      setShowCreateCustomerModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer account');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || isUpdating) return;
    setIsUpdating(true);
    try {
      const updateData = { ...formData };
      delete updateData.password; // Don't send password in update
      await api.put(`/users/${selectedUser.user_id}`, updateData);
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async (permanent = false) => {
    if (!selectedUser) return;
    try {
      if (permanent) {
        const response = await api.post(`/users/${selectedUser.user_id}/hard-delete`);
        const tenantDeleted = response.data.tenant_deleted;
        toast.success(
          tenantDeleted 
            ? 'User and their entire workspace permanently deleted. Email is now available for re-use.'
            : 'User permanently deleted. Email is now available for re-use.'
        );
      } else {
        await api.delete(`/users/${selectedUser.user_id}`);
        toast.success('User deactivated (soft delete). Email is still reserved.');
      }
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await api.post(`/users/${selectedUser.user_id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleImpersonate = async (user) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admins can impersonate users');
      return;
    }
    try {
      const res = await api.post(`/users/${user.user_id}/impersonate`);
      toast.success(`Now impersonating ${user.email}`);
      
      // Use the auth context to properly handle impersonation
      startImpersonation(res.data.access_token, res.data.user);
      
      // Full page reload to ensure clean state
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleEndTrial = async (user, targetPlan = 'keep') => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admins can end trials');
      return;
    }
    const label = user.company || user.email;
    const planName = targetPlan === 'keep' ? (user.plan || 'current') : targetPlan;
    const humanPlan = planName.charAt(0).toUpperCase() + planName.slice(1);
    const msg = targetPlan === 'keep'
      ? `End the trial for ${label} and keep them on ${humanPlan} at NO COST?\n\nThis stops all trial-ending emails and removes the 14-day cutoff, but their plan stays as ${humanPlan}.`
      : `End the trial for ${label} and move them to ${humanPlan} (free)?\n\nThis stops all trial-ending emails and removes the 14-day cutoff.`;
    if (!window.confirm(msg)) return;
    try {
      const query = targetPlan === 'keep' ? '' : `?plan=${encodeURIComponent(targetPlan)}`;
      await api.post(`/users/${user.user_id}/end-trial${query}`);
      toast.success(`Trial ended. Account is now ${humanPlan} at no cost.`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end trial');
    }
  };

  const openCreditsModal = async (user) => {
    setSelectedUser(user);
    setCreditsToAdd(10);
    setCreditsReason('');
    setCurrentCredits(null);
    setShowCreditsModal(true);
    
    // Fetch current credits for this tenant
    if (user.tenant_id) {
      try {
        const res = await api.get(`/ai/admin/credits/${user.tenant_id}`);
        setCurrentCredits(res.data.credits);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      }
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUser?.tenant_id) {
      toast.error('User does not have a tenant');
      return;
    }
    
    try {
      const res = await api.post('/ai/admin/add-credits', {
        tenant_id: selectedUser.tenant_id,
        credits: creditsToAdd,
        reason: creditsReason || undefined
      });
      toast.success(res.data.message);
      setShowCreditsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credits');
    }
  };

  // Translation settings functions
  const openTranslationModal = async (user) => {
    setSelectedUser(user);
    setTranslationSettings({
      translation_enabled: false,
      translation_tier: 'free',
      translation_positions: ['floating'],
      default_language: 'en',
      enabled_languages: null
    });
    setShowTranslationModal(true);
    
    // Fetch current translation settings for this tenant
    if (user.tenant_id) {
      try {
        const res = await api.get(`/admin/tenant/${user.tenant_id}/translation-settings`);
        setTranslationSettings({
          translation_enabled: res.data.translation_enabled || false,
          translation_tier: res.data.translation_tier || 'free',
          translation_positions: res.data.translation_positions || ['floating'],
          default_language: res.data.default_language || 'en',
          enabled_languages: res.data.enabled_languages,
          translation_mode: res.data.translation_mode || 'selector'
        });
      } catch (error) {
        console.error('Failed to fetch translation settings:', error);
      }
    }
  };

  const handleUpdateTranslation = async () => {
    if (!selectedUser?.tenant_id) {
      toast.error('User does not have a tenant');
      return;
    }
    
    setIsUpdatingTranslation(true);
    try {
      await api.put(`/admin/tenant/${selectedUser.tenant_id}/translation-settings`, translationSettings);
      toast.success(`Translation ${translationSettings.translation_enabled ? 'enabled' : 'disabled'} for ${selectedUser.company || selectedUser.email}`);
      setShowTranslationModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update translation settings');
    } finally {
      setIsUpdatingTranslation(false);
    }
  };

  // Wonde MIS functions
  const openWondeModal = async (user) => {
    setSelectedUser(user);
    setWondeStatus(null);
    setShowWondeModal(true);
    if (!user?.tenant_id) return;
    try {
      const res = await api.get(`/admin/wonde/status/${user.tenant_id}`);
      setWondeStatus(res.data);
    } catch (err) {
      setWondeStatus({ wonde_enabled: false, connected: false });
    }
  };

  const handleToggleWonde = async () => {
    if (!selectedUser?.tenant_id) return;
    setIsTogglingWonde(true);
    try {
      const res = await api.post(`/admin/wonde/toggle/${selectedUser.tenant_id}`);
      const enabled = res.data.wonde_enabled;
      setWondeStatus(prev => ({ ...prev, wonde_enabled: enabled }));
      toast.success(`Wonde ${enabled ? 'enabled' : 'disabled'} for ${selectedUser.company || selectedUser.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle Wonde');
    } finally {
      setIsTogglingWonde(false);
    }
  };

  // Helper: user has plan=reseller but no reseller_id set yet
  const isIncompleteReseller = (user) => user.plan === 'reseller' && !user.reseller_id;

  const openPromoteResellerModal = (user) => {
    setSelectedUser(user);
    const auto = (user.company || user.first_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    setPromoteResellerData({ company_name: user.company || '', slug: auto, commission_rate: '50' });
    setShowPromoteResellerModal(true);
  };

  const handlePromoteToReseller = async () => {
    if (!selectedUser || isPromoting) return;
    setIsPromoting(true);
    try {
      await api.post('/resellers/promote-user', {
        user_id: selectedUser.user_id,
        company_name: promoteResellerData.company_name,
        slug: promoteResellerData.slug,
        commission_rate: parseFloat(promoteResellerData.commission_rate) || 50,
      });
      toast.success(`${promoteResellerData.company_name} is now a full reseller account`);
      setShowPromoteResellerModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user to reseller');
    } finally {
      setIsPromoting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      company: '',
      role: 'editor',
      plan: 'starter',
      status: 'active',
      billing_type: 'free'
    });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      company: user.company || '',
      role: user.role || 'editor',
      plan: user.plan || 'starter',
      status: user.status || 'active',
      billing_type: user.billing_type || 'free'
    });
    setShowEditModal(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-700 border-green-200',
      inactive: 'bg-slate-100 text-slate-700 border-slate-200',
      suspended: 'bg-red-100 text-red-700 border-red-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
    };
    return variants[status] || variants.inactive;
  };

  const getRoleBadge = (role) => {
    const variants = {
      master_admin: 'bg-rose-100 text-rose-700 border-rose-200',
      super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
      admin: 'bg-blue-100 text-blue-700 border-blue-200',
      editor: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      viewer: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    return variants[role] || variants.viewer;
  };

  const getPlanBadge = (plan) => {
    const variants = {
      freemium: 'bg-green-100 text-green-600',
      free: 'bg-slate-100 text-slate-600',
      starter: 'bg-blue-100 text-blue-600',
      pro: 'bg-indigo-100 text-indigo-600',
      enterprise: 'bg-purple-100 text-purple-600',
      reseller: 'bg-orange-100 text-orange-600',
      inherited: 'bg-slate-50 text-slate-500 border border-slate-200',
    };
    return variants[plan] || variants.free;
  };

  // Small pill shown alongside the Plan pill when the user is still in
  // their 14-day trial. Highlights "Trial · Nd left" so admins can tell
  // trial-Enterprise from a paid Enterprise at a glance and know when to
  // convert them off. Days are computed client-side from
  // `trial_end_date` so it stays in sync without a server round-trip.
  const getTrialInfo = (user) => {
    if (!user.is_trial || !user.trial_end_date) return null;
    const endMs = new Date(user.trial_end_date).getTime();
    if (Number.isNaN(endMs)) return null;
    const daysLeft = Math.ceil((endMs - Date.now()) / 86400000);
    if (daysLeft <= 0) {
      return { label: 'Trial expired', tone: 'bg-red-100 text-red-700 border border-red-200' };
    }
    if (daysLeft <= 3) {
      return { label: `Trial · ${daysLeft}d left`, tone: 'bg-orange-100 text-orange-700 border border-orange-200' };
    }
    return { label: `Trial · ${daysLeft}d left`, tone: 'bg-amber-100 text-amber-700 border border-amber-200' };
  };

  // Helper to determine if user is a team member (has tenant_id and non-admin role)
  const isTeamMember = (user) => {
    return user.tenant_id && !['admin', 'super_admin', 'master_admin'].includes(user.role);
  };

  // Get display plan - team members inherit from company
  const getDisplayPlan = (user) => {
    if (isTeamMember(user)) {
      return { plan: 'inherited', label: 'Team Member' };
    }
    return { plan: user.plan || 'starter', label: user.plan || 'starter' };
  };

  // If not super admin, show access denied (AFTER all hooks)
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500 mb-4">You need Super Admin privileges to access User Management.</p>
            <p className="text-sm text-slate-400">Use the Team page to manage your organization&apos;s team members.</p>
            <Button onClick={() => navigate('/team')} className="mt-4">
              Go to Team Management
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <UserCog className="text-indigo-600" size={32} />
              User Management
            </h1>
            <p className="text-slate-500 mt-1">
              {isSuperAdmin ? 'Manage all users across the platform' : 'Manage users in your organization'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }} variant="outline" className="gap-2">
              <Plus size={18} />
              Add Team Member
            </Button>
            <Button onClick={() => { resetForm(); setShowCreateCustomerModal(true); }} className="gap-2">
              <Crown size={18} />
              Create New Customer
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
              <SelectTrigger className="w-48">
                <ArrowUpDown size={16} className="mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40">
                <Shield size={16} className="mr-2" />
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Plan Filter */}
            <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40">
                <Crown size={16} className="mr-2" />
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                {PLANS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">User</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Role</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Plan</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Billing</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Date Joined</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.user_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                            {(user.first_name?.[0] || user.full_name?.[0] || user.email[0]).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">
                                {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : (user.full_name || user.email)}
                              </p>
                              {isSuperAdmin && currentUser?.tenant_id && user.tenant_id === currentUser.tenant_id && user.user_id !== currentUser.user_id && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
                                  <Users size={10} />
                                  Team
                                </span>
                              )}
                            </div>
                            {isSuperAdmin && user.tenant_name && (
                              <p className="text-xs text-slate-500">
                                {user.tenant_name}
                                {user.tenant_id && (
                                  <span className="ml-2 text-slate-400 font-mono" title="Tenant ID">
                                    ({user.tenant_id.substring(0, 8)}...)
                                  </span>
                                )}
                              </p>
                            )}
                            {isSuperAdmin && !user.tenant_name && user.tenant_id && (
                              <p className="text-xs text-slate-400 font-mono" title="Tenant ID">
                                ID: {user.tenant_id.substring(0, 8)}...
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadge(user.role)}`}>
                          {user.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{user.company || '-'}</td>
                      <td className="px-6 py-4">
                        {(() => {
                          const displayPlan = getDisplayPlan(user);
                          const trial = getTrialInfo(user);
                          return (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanBadge(displayPlan.plan)}`}>
                                {displayPlan.label}
                              </span>
                              {trial && (
                                <span
                                  className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${trial.tone}`}
                                  title={user.trial_end_date ? `Ends ${new Date(user.trial_end_date).toLocaleDateString()}` : ''}
                                  data-testid={`user-trial-badge-${user.user_id}`}
                                >
                                  {trial.label}
                                </span>
                              )}
                              {isIncompleteReseller(user) && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-orange-100 text-orange-700 border border-orange-200 cursor-pointer"
                                  title="Reseller plan set but not fully provisioned — click to complete setup"
                                  onClick={() => openPromoteResellerModal(user)}
                                  data-testid={`incomplete-reseller-badge-${user.user_id}`}
                                >
                                  <AlertTriangle size={10} />
                                  Setup Incomplete
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadge(user.status)}`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.billing_type === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {user.billing_type === 'paid' ? 'Paid' : 'Free'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {isSuperAdmin && (
                              <>
                                <DropdownMenuItem onClick={() => handleImpersonate(user)} className="gap-2">
                                  <LogIn size={16} />
                                  Impersonate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openCreditsModal(user)} className="gap-2">
                                  <Coins size={16} />
                                  Manage Credits
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openTranslationModal(user)} className="gap-2">
                                  <Languages size={16} />
                                  Translation Settings
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openWondeModal(user)} className="gap-2">
                                  <School size={16} />
                                  Wonde MIS Settings
                                </DropdownMenuItem>
                                {isIncompleteReseller(user) && (
                                  <DropdownMenuItem
                                    onClick={() => openPromoteResellerModal(user)}
                                    className="gap-2 text-orange-600 focus:text-orange-600"
                                    data-testid={`promote-reseller-btn-${user.user_id}`}
                                  >
                                    <Crown size={16} />
                                    Complete Reseller Setup
                                  </DropdownMenuItem>
                                )}
                                {user.is_trial && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => handleEndTrial(user, 'keep')}
                                      className="gap-2 text-emerald-700 focus:text-emerald-700"
                                      data-testid={`end-trial-keep-${user.user_id}`}
                                    >
                                      <ShieldCheck size={16} />
                                      End Trial (keep {user.plan || 'current'} · $0)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleEndTrial(user, 'freemium')}
                                      className="gap-2"
                                      data-testid={`end-trial-freemium-${user.user_id}`}
                                    >
                                      <ShieldCheck size={16} />
                                      End Trial → Move to Freemium
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem onClick={() => { setSelectedUser(user); setShowResetPasswordModal(true); }} className="gap-2">
                              <Key size={16} />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditModal(user)} className="gap-2">
                              <Edit size={16} />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                              className="gap-2 text-red-600 focus:text-red-600"
                            >
                              <Trash2 size={16} />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} users
              </span>
              <Select value={perPage.toString()} onValueChange={(v) => { setPerPage(parseInt(v)); setPage(1); }}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map(size => (
                    <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">per page</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Create Team Member Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>
                Add a new team member to <strong>your organization</strong>. They will share access to your pages, forms, and content.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Inc."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {isMasterAdmin && <SelectItem value="master_admin">Master Admin</SelectItem>}
                      {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Only show Plan for admin/super_admin roles - team members inherit company plan */}
                {['admin', 'super_admin', 'master_admin'].includes(formData.role) ? (
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <Select value={formData.plan} onValueChange={(v) => setFormData({ ...formData, plan: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freemium">Freemium (Free Forever)</SelectItem>
                        <SelectItem value="free">Free (Legacy)</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                        <SelectItem value="reseller">Reseller</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500 flex items-center">
                      Inherits company plan
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Billing</Label>
                  <div className="flex items-center gap-3 h-10">
                    <span className={`text-sm ${formData.billing_type === 'free' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>Free</span>
                    <Switch
                      checked={formData.billing_type === 'paid'}
                      onCheckedChange={(checked) => setFormData({ ...formData, billing_type: checked ? 'paid' : 'free' })}
                    />
                    <span className={`text-sm ${formData.billing_type === 'paid' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>Paid</span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={isCreating}>Cancel</Button>
              <Button onClick={handleCreateUser} disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Add Team Member'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create New Customer Modal - Creates a completely separate tenant */}
        <Dialog open={showCreateCustomerModal} onOpenChange={setShowCreateCustomerModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown size={20} className="text-amber-500" />
                Create New Customer Account
              </DialogTitle>
              <DialogDescription>
                This creates a <strong>completely independent account</strong> with its own tenant, workspace, and data. 
                The customer will start with a 14-day free trial.
              </DialogDescription>
            </DialogHeader>
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle size={16} className="text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Important:</strong> This is NOT for adding team members to your organization. 
                Use "Add Team Member" for that. This creates a separate customer account that will have their own pages, forms, and content.
              </AlertDescription>
            </Alert>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="customer@school.edu"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>Organization / School Name *</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Springfield Elementary School"
                />
                <p className="text-xs text-slate-500">This will be used as the customer's tenant/workspace name</p>
              </div>
              <div className="space-y-2">
                <Label>Starting Plan</Label>
                <Select value={formData.plan} onValueChange={(v) => setFormData({ ...formData, plan: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freemium">Freemium (Free Forever - Limited)</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Customer will start with a 14-day trial regardless of plan</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateCustomerModal(false)} disabled={isCreating}>Cancel</Button>
              <Button onClick={handleCreateCustomer} disabled={isCreating} className="bg-amber-500 hover:bg-amber-600">
                {isCreating ? 'Creating...' : 'Create Customer Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {isMasterAdmin && <SelectItem value="master_admin">Master Admin</SelectItem>}
                      {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Only show Plan for admin/super_admin roles - team members inherit company plan */}
                {['admin', 'super_admin', 'master_admin'].includes(formData.role) ? (
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <Select value={formData.plan} onValueChange={(v) => setFormData({ ...formData, plan: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freemium">Freemium (Free Forever)</SelectItem>
                        <SelectItem value="free">Free (Legacy)</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                        <SelectItem value="reseller">Reseller</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500 flex items-center">
                      Inherits company plan
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Billing</Label>
                  <div className="flex items-center gap-3 h-10">
                    <span className={`text-sm ${formData.billing_type === 'free' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>Free</span>
                    <Switch
                      checked={formData.billing_type === 'paid'}
                      onCheckedChange={(checked) => setFormData({ ...formData, billing_type: checked ? 'paid' : 'free' })}
                    />
                    <span className={`text-sm ${formData.billing_type === 'paid' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>Paid</span>
                  </div>
                </div>
              </div>
              
              {/* Tenant ID - Read Only */}
              {selectedUser?.tenant_id && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <Label className="text-slate-500">Tenant ID</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono text-slate-600 select-all">
                      {selectedUser.tenant_id}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedUser.tenant_id);
                        toast.success('Tenant ID copied to clipboard');
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">This is the unique identifier for this user's workspace</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={isUpdating}>Cancel</Button>
              <Button onClick={handleUpdateUser} disabled={isUpdating}>
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Choose how you want to remove this user from the system.
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertDescription className="text-amber-800">
                    User to delete: <strong>{selectedUser.email}</strong>
                    {selectedUser.company && <span className="block text-sm mt-1">Organization: {selectedUser.company}</span>}
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-3">
                  <div className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => handleDeleteUser(false)}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <X size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">Deactivate (Soft Delete)</p>
                        <p className="text-sm text-slate-500">Sets status to inactive. Email remains reserved. Can be reactivated later.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 border border-red-200 rounded-lg hover:bg-red-50 cursor-pointer" onClick={() => handleDeleteUser(true)}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <Trash2 size={20} className="text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-red-700">Permanently Delete</p>
                        <p className="text-sm text-red-600">Completely removes user and frees up email for re-use. If they are the only user in their org, all their data will be deleted too.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Modal */}
        <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowResetPasswordModal(false); setNewPassword(''); }}>Cancel</Button>
              <Button onClick={handleResetPassword}>Reset Password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Credits Modal */}
        <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Coins className="text-amber-500" size={24} />
                Manage AI Generations
              </DialogTitle>
              <DialogDescription>
                Add or subtract top-up AI Generations for {selectedUser?.full_name || selectedUser?.email}&apos;s account
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {currentCredits !== null && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Top-up Balance (never expires)</p>
                  <p className="text-2xl font-bold text-slate-900">{currentCredits} generations</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Action</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={creditsToAdd >= 0 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCreditsToAdd(Math.abs(creditsToAdd))}
                    className={creditsToAdd >= 0 ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    Add Credits
                  </Button>
                  <Button
                    type="button"
                    variant={creditsToAdd < 0 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCreditsToAdd(-Math.abs(creditsToAdd || 10))}
                    className={creditsToAdd < 0 ? "bg-red-600 hover:bg-red-700" : ""}
                  >
                    Subtract Credits
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Amount</Label>
                <div className="flex gap-2">
                  {[10, 25, 50, 100, 200].map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={Math.abs(creditsToAdd) === amount ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCreditsToAdd(creditsToAdd < 0 ? -amount : amount)}
                    >
                      {amount}
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  min="1"
                  value={Math.abs(creditsToAdd)}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setCreditsToAdd(creditsToAdd < 0 ? -val : val);
                  }}
                  placeholder="Custom amount"
                  className="mt-2"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Textarea
                  value={creditsReason}
                  onChange={(e) => setCreditsReason(e.target.value)}
                  placeholder="e.g., Promotional credits, Refund, etc."
                  rows={2}
                />
              </div>

              {currentCredits !== null && (
                <div className={`p-3 rounded-lg border ${
                  creditsToAdd >= 0 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <p className={`text-sm ${creditsToAdd >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    New balance will be: <strong>{Math.max(0, currentCredits + creditsToAdd)} generations</strong>
                    {creditsToAdd < 0 && currentCredits + creditsToAdd < 0 && (
                      <span className="block text-xs mt-1">
                        (Cannot go below 0 - will remove {currentCredits} generations)
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreditsModal(false)}>Cancel</Button>
              <Button 
                onClick={handleAddCredits} 
                disabled={creditsToAdd === 0}
                className={creditsToAdd < 0 ? "bg-red-600 hover:bg-red-700" : ""}
              >
                <Coins size={16} className="mr-2" />
                {creditsToAdd >= 0 ? `Add ${creditsToAdd} Generations` : `Remove ${Math.abs(creditsToAdd)} Generations`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Translation Settings Modal */}
        <Dialog open={showTranslationModal} onOpenChange={setShowTranslationModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Languages className="text-blue-500" size={24} />
                Translation Settings
              </DialogTitle>
              <DialogDescription>
                Configure automatic translation for {selectedUser?.company || selectedUser?.email}&apos;s website
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Enable/Disable Translation Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Translation</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Allow visitors to translate pages into their language
                  </p>
                </div>
                <Switch
                  checked={translationSettings.translation_enabled}
                  onCheckedChange={(checked) => setTranslationSettings({ ...translationSettings, translation_enabled: checked })}
                />
              </div>

              {translationSettings.translation_enabled && (
                <>
                  {/* Translation Tier */}
                  <div className="space-y-2">
                    <Label>Translation Tier</Label>
                    <Select 
                      value={translationSettings.translation_tier} 
                      onValueChange={(v) => setTranslationSettings({ ...translationSettings, translation_tier: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free (Google Widget)</SelectItem>
                        <SelectItem value="premium">Premium (API - Higher Quality)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {translationSettings.translation_tier === 'free' 
                        ? 'Uses Google Translate browser widget - no API costs'
                        : 'Uses Google Cloud Translation API - cached for cost efficiency'}
                    </p>
                  </div>

                  {/* Translation Positions */}
                  <div className="space-y-2">
                    <Label>Selector Positions</Label>
                    <div className="space-y-2">
                      {[
                        { id: 'header', label: 'Header (Right Side)', desc: 'In the navigation menu' },
                        { id: 'footer', label: 'Footer', desc: 'At the bottom of the page' },
                        { id: 'floating', label: 'Floating', desc: 'Fixed position (top-left corner)' }
                      ].map((pos) => (
                        <div key={pos.id} className="flex items-center gap-3 p-2 rounded border border-slate-200">
                          <input
                            type="checkbox"
                            id={`pos-${pos.id}`}
                            checked={translationSettings.translation_positions?.includes(pos.id)}
                            onChange={(e) => {
                              const positions = translationSettings.translation_positions || [];
                              if (e.target.checked) {
                                setTranslationSettings({
                                  ...translationSettings,
                                  translation_positions: [...positions, pos.id]
                                });
                              } else {
                                setTranslationSettings({
                                  ...translationSettings,
                                  translation_positions: positions.filter(p => p !== pos.id)
                                });
                              }
                            }}
                            className="rounded"
                          />
                          <div>
                            <label htmlFor={`pos-${pos.id}`} className="text-sm font-medium cursor-pointer">
                              {pos.label}
                            </label>
                            <p className="text-xs text-slate-500">{pos.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Default Language */}
                  <div className="space-y-2">
                    <Label>Default Language</Label>
                    <Select 
                      value={translationSettings.default_language} 
                      onValueChange={(v) => setTranslationSettings({ ...translationSettings, default_language: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="it">Italian</SelectItem>
                        <SelectItem value="pt">Portuguese</SelectItem>
                        <SelectItem value="zh">Chinese</SelectItem>
                        <SelectItem value="ja">Japanese</SelectItem>
                        <SelectItem value="ko">Korean</SelectItem>
                        <SelectItem value="ar">Arabic</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                        <SelectItem value="pl">Polish</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">The original language of the website content</p>
                  </div>

                  {/* Translation Mode */}
                  <div className="space-y-2">
                    <Label>Auto-Translation Mode</Label>
                    <Select 
                      value={translationSettings.translation_mode || 'selector'} 
                      onValueChange={(v) => setTranslationSettings({ ...translationSettings, translation_mode: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="selector">Manual Selection Only</SelectItem>
                        <SelectItem value="auto">Auto-detect (Browser Language)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {translationSettings.translation_mode === 'auto' 
                        ? 'Automatically translates based on visitor\'s browser language settings'
                        : 'Visitors must manually select their language using the selector'}
                    </p>
                  </div>

                  {/* Info Box */}
                  <Alert className={translationSettings.translation_tier === 'premium' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}>
                    <Languages size={16} className={translationSettings.translation_tier === 'premium' ? 'text-purple-600' : 'text-blue-600'} />
                    <AlertDescription className={translationSettings.translation_tier === 'premium' ? 'text-purple-800' : 'text-blue-800'}>
                      <strong>{translationSettings.translation_tier === 'premium' ? 'Premium Translation' : 'Free Translation'}</strong>
                      <br />
                      {translationSettings.translation_tier === 'premium' 
                        ? 'Higher quality translations with caching. API costs apply but are minimized through intelligent caching.'
                        : 'Uses browser-based Google Translate. Free but may show Google branding.'}
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTranslationModal(false)} disabled={isUpdatingTranslation}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateTranslation} 
                disabled={isUpdatingTranslation}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Languages size={16} className="mr-2" />
                {isUpdatingTranslation ? 'Saving...' : 'Save Settings'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Wonde MIS Settings Modal */}
        <Dialog open={showWondeModal} onOpenChange={setShowWondeModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <School size={20} className="text-green-600" />
                Wonde MIS Settings
              </DialogTitle>
              <DialogDescription>
                Enable or disable Wonde MIS sync for {selectedUser?.company || selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {wondeStatus === null ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                  <Loader2 size={16} className="animate-spin" />
                  Loading Wonde status...
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <div className="text-sm font-medium text-slate-800">Wonde Integration</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Allow this tenant to sync parent contacts from Wonde MIS
                      </div>
                    </div>
                    <Switch
                      checked={wondeStatus?.wonde_enabled || false}
                      onCheckedChange={handleToggleWonde}
                      disabled={isTogglingWonde}
                      data-testid="wonde-admin-toggle"
                    />
                  </div>

                  {wondeStatus?.connected && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                        <CheckCircle size={14} />
                        School Connected
                      </div>
                      <div className="text-slate-600">{wondeStatus.school_name}</div>
                      {wondeStatus.last_sync_at && (
                        <div className="text-xs text-slate-400 mt-1">
                          Last sync: {new Date(wondeStatus.last_sync_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}

                  {wondeStatus?.wonde_enabled && !wondeStatus?.connected && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      Wonde enabled but not yet configured. The tenant admin will need to connect their school via Newsletters → Contact Sync.
                    </div>
                  )}

                  {!wondeStatus?.wonde_enabled && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500">
                      When enabled, this tenant can connect their Wonde account and sync parent contacts into subscriber lists grouped by year.
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWondeModal(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Promote to Reseller Modal */}
        <Dialog open={showPromoteResellerModal} onOpenChange={setShowPromoteResellerModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown size={20} className="text-orange-500" />
                Complete Reseller Setup
              </DialogTitle>
              <DialogDescription>
                This will create a full reseller account for <strong>{selectedUser?.company || selectedUser?.email}</strong> and give them access to the Reseller Dashboard.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-4">
              <div className="space-y-2">
                <Label>Company / Reseller Name *</Label>
                <Input
                  value={promoteResellerData.company_name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                    setPromoteResellerData(d => ({ ...d, company_name: name, slug: autoSlug }));
                  }}
                  placeholder="Acme Schools Ltd"
                  data-testid="promote-reseller-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Portal Slug *</Label>
                <Input
                  value={promoteResellerData.slug}
                  onChange={(e) => setPromoteResellerData(d => ({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                  placeholder="acme-schools"
                  data-testid="promote-reseller-slug"
                />
                <p className="text-xs text-slate-400">URL-friendly identifier — lowercase letters, numbers, hyphens only</p>
              </div>
              <div className="space-y-2">
                <Label>Commission Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={promoteResellerData.commission_rate}
                  onChange={(e) => setPromoteResellerData(d => ({ ...d, commission_rate: e.target.value }))}
                  placeholder="50"
                  data-testid="promote-reseller-commission"
                />
                <p className="text-xs text-slate-400">Revenue share percentage this reseller earns per customer</p>
              </div>
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700 space-y-1">
                <p className="font-medium">What happens next:</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>A reseller record is created and linked to this account</li>
                  <li>The user gets access to the Reseller Dashboard</li>
                  <li>A demo workspace is auto-created on first login</li>
                  <li>They can start creating and managing customer tenants</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPromoteResellerModal(false)} disabled={isPromoting}>
                Cancel
              </Button>
              <Button
                onClick={handlePromoteToReseller}
                disabled={isPromoting || !promoteResellerData.company_name || !promoteResellerData.slug}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="promote-reseller-confirm-btn"
              >
                {isPromoting ? 'Setting up...' : 'Promote to Reseller'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UserManagement;
